import {Box} from '@mui/material';
import {useEffect, useMemo, useState, useCallback} from 'react';
import {GridColDef} from '@mui/x-data-grid';
import {getIntercomDeviceAuditLogs} from 'src/apis/autoGenerate/services';
import {IntercomDeviceLog} from 'src/apis/autoGenerate/types/intercomDeviceLog';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {useOrgId} from 'src/hooks';
import {useAppSelector} from 'src/redux/hooks';
import {selectDevice} from 'src/redux/slice/deviceSlice';
import {selectProperty} from 'src/redux/slice/propertySlice';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import placeholderImg from '../../../assets/placeholder_door_event.png';
import EventDetails from 'src/components/EventDetails';
import SummaryBar, {SummaryStatItem} from 'src/components/SummaryBar';
import EventFilterBar, {
  DateRangePreset,
  EventTypeFilter,
  StatusFilter,
} from 'src/components/EventFilterBar';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SensorDoorOutlinedIcon from '@mui/icons-material/SensorDoorOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import {EntioDataGrid, StatusBadge, getEventStatusVariant} from 'src/components/ui';
import {brand} from 'src/theme';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const PAGE_SIZE = 10;

function getEventTypeInfo(log: IntercomDeviceLog) {
  const operationType = log.operation_type;
  if (operationType === 'VIRTUAL_PIN_VERIFY') {
    const pinType = log.metadata?.pinType;
    switch (pinType) {
      case 'room':
        return {label: 'Room PIN Access', icon: 'pin', color: brand[500]};
      case 'delivery':
        return {label: 'Delivery Access', icon: 'pin', color: brand[500]};
      case 'guest':
        return {label: 'Guest Access', icon: 'pin', color: brand[500]};
      default:
        return {label: 'Virtual Pin Access', icon: 'pin', color: brand[500]};
    }
  }
  if (
    operationType === 'DOOR_ACCESS' &&
    log.metadata?.accessMethod === 'ADMIN_TEST'
  ) {
    return {label: 'Admin Test', icon: 'door', color: brand[500]};
  }
  if (
    operationType === 'DOOR_ACCESS' &&
    (log.metadata?.accessMethod === 'FRONT_DESK' || log.metadata?.source === 'FRONT_DESK')
  ) {
    return {label: 'Front Desk', icon: 'door', color: brand[500]};
  }
  return {label: 'Door Open', icon: 'door', color: brand[500]};
}

function getUserDisplay(log: IntercomDeviceLog) {
  if (log.entrio_users) {
    const firstName = log.entrio_users.first_name || '';
    const lastName = log.entrio_users.last_name || '';
    const full = `${firstName} ${lastName}`.trim();
    return full || '-';
  }
  if (log.operation_type === 'VIRTUAL_PIN_VERIFY' && log.metadata) {
    if (log.metadata.deliveryName) return log.metadata.deliveryName;
    if (log.metadata.guestName) return log.metadata.guestName.trim();
    if (log.metadata.roomName) return `Room ${log.metadata.roomName}`;
  }
  return '-';
}

function getRelativeTime(dateTime: string, deviceTimezone?: string) {
  if (!dateTime) return '-';
  try {
    const tz = deviceTimezone || 'UTC';
    const utcTime = dayjs.utc(dateTime);
    const now = dayjs();
    const diffMinutes = now.diff(utcTime, 'minute');
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const diffHours = now.diff(utcTime, 'hour');
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = now.diff(utcTime, 'day');
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return dayjs.utc(dateTime).tz(tz).format('MMM DD, YYYY');
  } catch {
    return dayjs.utc(dateTime).format('MMM DD, YYYY');
  }
}

export default function DoorEvent() {
  const [searchApartment, setSearchApartment] = useState<any>(null);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('30d');
  const [eventTypeFilter, setEventTypeFilter] = useState<EventTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [allLogs, setAllLogs] = useState<IntercomDeviceLog[]>([]);
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: PAGE_SIZE});
  const [selectedEvent, setSelectedEvent] = useState<IntercomDeviceLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const deviceState = useAppSelector(selectDevice);
  const property = useAppSelector(selectProperty);
  const {orgId} = useOrgId();

  const dateRange = useMemo(() => {
    const end = dayjs();
    switch (dateRangePreset) {
      case '7d':
        return {start: end.subtract(7, 'day'), end};
      case '30d':
        return {start: end.subtract(30, 'day'), end};
      case '90d':
        return {start: end.subtract(90, 'day'), end};
      case 'all':
      default:
        return {start: null, end: null};
    }
  }, [dateRangePreset]);

  const fetchLogs = async () => {
    try {
      if (!orgId) {
        setLoading(false);
        return;
      }
      if (property.loadingList) {
        setLoading(false);
        return;
      }

      setLoading(true);

      // Send full ISO timestamps so the backend filter is inclusive of "now"
      // (a YYYY-MM-DD endDate is parsed as 00:00 UTC on the server, which
      // would exclude events that happened later in the day).
      const startDate = dateRange.start?.toISOString();
      const endDate = dateRange.end?.toISOString();

      const operationTypes = ['DOOR_ACCESS', 'VIRTUAL_PIN_VERIFY'];

      if (searchApartment?.id) {
        const logPromises = operationTypes.map(opType =>
          getIntercomDeviceAuditLogs(
            orgId,
            opType,
            undefined,
            startDate,
            endDate,
            '1',
            '1000',
            undefined,
            searchApartment.id
          )
        );
        const results = await Promise.allSettled(logPromises);
        let merged: IntercomDeviceLog[] = [];
        results.forEach(result => {
          if (result.status === 'fulfilled' && result.value?.data?.data?.logs) {
            merged = [...merged, ...result.value.data.data.logs];
          }
        });
        merged.sort(
          (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        );
        setAllLogs(merged);
      } else {
        const apartmentIds = property.apartmentList
          .map(apt => apt.id)
          .filter(Boolean) as string[];

        if (apartmentIds.length === 0) {
          setAllLogs([]);
          setLoading(false);
          return;
        }

        const logPromises = apartmentIds.flatMap(apartmentId =>
          operationTypes.map(opType =>
            getIntercomDeviceAuditLogs(
              orgId,
              opType,
              undefined,
              startDate,
              endDate,
              '1',
              '1000',
              undefined,
              apartmentId
            )
          )
        );

        const results = await Promise.allSettled(logPromises);
        let merged: IntercomDeviceLog[] = [];
        results.forEach(result => {
          if (result.status === 'fulfilled' && result.value?.data?.data?.logs) {
            merged = [...merged, ...result.value.data.data.logs];
          }
        });
        merged.sort(
          (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        );
        setAllLogs(merged);
      }
    } catch (error) {
      console.error('Error fetching door event logs:', error);
      setAllLogs([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line
  }, [orgId, property.loadingList, searchApartment, dateRange.start?.valueOf(), dateRange.end?.valueOf()]);

  const filteredLogs = useMemo(() => {
    let result = allLogs;

    if (eventTypeFilter !== 'all') {
      result = result.filter(log => {
        const info = getEventTypeInfo(log);
        switch (eventTypeFilter) {
          case 'door_open':
            return log.operation_type === 'DOOR_ACCESS';
          case 'pin_access':
            return log.operation_type === 'VIRTUAL_PIN_VERIFY' && log.metadata?.pinType === 'room';
          case 'guest_access':
            return log.operation_type === 'VIRTUAL_PIN_VERIFY' && log.metadata?.pinType === 'guest';
          case 'delivery':
            return (
              log.operation_type === 'VIRTUAL_PIN_VERIFY' && log.metadata?.pinType === 'delivery'
            );
          default:
            return true;
        }
      });
    }

    if (statusFilter !== 'all') {
      result = result.filter(
        log => (log.status || '').toLowerCase() === statusFilter
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(log => {
        const userName = getUserDisplay(log).toLowerCase();
        const deviceName = (log.device?.name || '').toLowerCase();
        return userName.includes(q) || deviceName.includes(q);
      });
    }

    return result;
  }, [allLogs, eventTypeFilter, statusFilter, searchQuery]);

  const paginatedLogs = useMemo(() => {
    const startIdx = paginationModel.page * paginationModel.pageSize;
    return filteredLogs.slice(startIdx, startIdx + paginationModel.pageSize);
  }, [filteredLogs, paginationModel]);

  const summaryStats: SummaryStatItem[] = useMemo(() => {
    const total = filteredLogs.length;
    const success = filteredLogs.filter(l => (l.status || '').toLowerCase() === 'success').length;
    const failed = filteredLogs.filter(l => (l.status || '').toLowerCase() !== 'success').length;
    const pinCount = filteredLogs.filter(l => l.operation_type === 'VIRTUAL_PIN_VERIFY').length;
    const doorCount = filteredLogs.filter(l => l.operation_type === 'DOOR_ACCESS').length;

    return [
      {label: 'Total Events', value: total, color: 'default' as const},
      {
        label: 'Success',
        value: success,
        color: 'success' as const,
        icon: <CheckCircleOutlineIcon sx={{fontSize: 18}} />,
      },
      {
        label: 'Failed',
        value: failed,
        color: 'error' as const,
        icon: <ErrorOutlineIcon sx={{fontSize: 18}} />,
      },
      {
        label: 'PIN',
        value: pinCount,
        color: 'primary' as const,
        icon: <VpnKeyOutlinedIcon sx={{fontSize: 18}} />,
      },
      {
        label: 'Door',
        value: doorCount,
        color: 'info' as const,
        icon: <SensorDoorOutlinedIcon sx={{fontSize: 18}} />,
      },
    ];
  }, [filteredLogs]);

  const handleApartmentChange = (val: Apartment | null) => {
    setSearchApartment(val ?? null);
    setPaginationModel(prev => ({...prev, page: 0}));
  };

  const handleRowClick = (params: any) => {
    setSelectedEvent(params.row);
    setDetailsOpen(true);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'preview',
        headerName: 'Preview',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: params => {
          const photoUrl = params.row.photo?.photoUrl;
          const imgSrc = photoUrl || placeholderImg;
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Box
                component="img"
                src={imgSrc}
                alt="preview"
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 1.5,
                  objectFit: 'cover',
                }}
              />
            </Box>
          );
        },
      },
      {
        field: 'event',
        headerName: 'Event',
        flex: 1.2,
        minWidth: 160,
        sortable: false,
        renderCell: params => {
          const info = getEventTypeInfo(params.row);
          return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, height: '100%'}}>
              {info.icon === 'door' ? (
                <SensorDoorOutlinedIcon sx={{fontSize: 18, color: info.color}} />
              ) : (
                <VpnKeyOutlinedIcon sx={{fontSize: 18, color: info.color}} />
              )}
              <Box sx={{fontWeight: 500, fontSize: '0.875rem'}}>{info.label}</Box>
            </Box>
          );
        },
      },
      {
        field: 'user',
        headerName: 'User',
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueGetter: params => getUserDisplay(params.row),
      },
      {
        field: 'method',
        headerName: 'Method',
        flex: 0.8,
        minWidth: 100,
        sortable: false,
        valueGetter: params => {
          const method = params.row.metadata?.accessMethod;
          if (method === 'FRONT_DESK' || params.row.metadata?.source === 'FRONT_DESK') {
            return 'Front Desk';
          }
          return method || '-';
        },
      },
      {
        field: 'device',
        headerName: 'Device',
        flex: 0.9,
        minWidth: 120,
        sortable: false,
        valueGetter: params => params.row.device?.name || '-',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: params => {
          const s = (params.row.status || '').toLowerCase();
          return (
            <StatusBadge
              status={getEventStatusVariant(s)}
              label={s === 'success' ? 'SUCCESS' : 'FAILED'}
            />
          );
        },
      },
      {
        field: 'time',
        headerName: 'Time',
        width: 120,
        sortable: false,
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const relTime = getRelativeTime(
            params.row.created_at,
            params.row.device?.timezone
          );
          return (
            <Box sx={{fontSize: '0.8rem', color: 'text.secondary'}}>
              {relTime}
            </Box>
          );
        },
      },
    ],
    []
  );

  return (
    <Box>
      {/* Filter Bar */}
      <EventFilterBar
        dateRange={dateRangePreset}
        onDateRangeChange={v => {
          setDateRangePreset(v);
          setPaginationModel(prev => ({...prev, page: 0}));
        }}
        eventType={eventTypeFilter}
        onEventTypeChange={v => {
          setEventTypeFilter(v);
          setPaginationModel(prev => ({...prev, page: 0}));
        }}
        statusFilter={statusFilter}
        onStatusFilterChange={v => {
          setStatusFilter(v);
          setPaginationModel(prev => ({...prev, page: 0}));
        }}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onApartmentChange={handleApartmentChange}
      />

      {/* Summary Bar */}
      <Box mt={2}>
        <SummaryBar stats={summaryStats} />
      </Box>

      {/* Data Grid */}
      <Box mt={2}>
        <EntioDataGrid
          loading={loading}
          rows={paginatedLogs.map((log, idx) => ({...log, id: log.id || idx}))}
          columns={columns}
          rowCount={filteredLogs.length}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={handleRowClick}
          pageSizeOptions={[5, 10, 25]}
          richRows
          emptyMessage="No door events"
          emptyDescription="Door access events will appear here once your devices are connected."
        />
      </Box>

      <EventDetails
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
        eventType="door"
      />
    </Box>
  );
}
